import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from 'i18next';

type Language = 'en' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('cevee-language');
    if (saved === 'en' || saved === 'de') {
      return saved;
    }
    const browserLang = navigator.language.split('-')[0];
    const defaultLang: Language = browserLang === 'de' ? 'de' : 'en';
    return defaultLang;
  });

  // Keep i18n in sync with our context (covers the case where i18n was
  // initialised from its own detector but our context resolved to a
  // different language from localStorage). Without this, useTranslation()
  // strings and our manual `lang === 'de'` checks can drift apart.
  useEffect(() => {
    if (i18n.language !== language) {
      try { i18n.changeLanguage(language); } catch {}
    }
    // Also reflect the active language on <html lang="..."> for a11y.
    try { document.documentElement.lang = language; } catch {}
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('cevee-language', lang);
    try { i18n.changeLanguage(lang); } catch {}
  };

  const toggleLanguage = () => {
    const newLang: Language = language === 'en' ? 'de' : 'en';
    setLanguage(newLang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
